'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type PropsWithChildren,
} from 'react';
import dynamic from 'next/dynamic';

import { SkeletonLoader } from '@/shared/ui/skeleton/skeleton';

import {
  AuthLoadingBackdrop,
  AuthLoadingCard,
  AuthLoadingRoot,
} from './dynamic-styles';
import type { AuthManagementProps } from './index';

interface AuthLoadingContextValue {
  visible: boolean;
  onClose: () => void;
}

interface AuthManagementLoadingProps {
  error?: Error | null;
  isLoading?: boolean;
}

const AuthLoadingContext = createContext<AuthLoadingContextValue | null>(null);

const AuthLoadingProvider = ({
  children,
  value,
}: PropsWithChildren<{ value: AuthLoadingContextValue }>) => (
  <AuthLoadingContext.Provider value={value}>
    {children}
  </AuthLoadingContext.Provider>
);

const AuthManagementLoading = ({
  error,
  isLoading,
}: AuthManagementLoadingProps) => {
  const context = useContext(AuthLoadingContext);

  useEffect(() => {
    if (error && context?.visible) {
      context.onClose();
    }
  }, [context, error]);

  if (context?.visible !== true || error != null || isLoading !== true) {
    return null;
  }

  return (
    <AuthLoadingRoot>
      <AuthLoadingBackdrop
        type="button"
        aria-label="Close authentication loading"
        onClick={context.onClose}
      />
      <AuthLoadingCard role="status" aria-live="polite">
        <SkeletonLoader width="100%" height="100%" borderRadius={0} />
      </AuthLoadingCard>
    </AuthLoadingRoot>
  );
};

const DynamicAuthManagementContent = dynamic(
  async () => import('./index').then(({ AuthManagement }) => AuthManagement),
  {
    ssr: false,
    loading: (props) => <AuthManagementLoading {...props} />,
  }
);

const AuthManagementDynamic = (props: AuthManagementProps) => {
  const [hasMounted, setHasMounted] = useState(props.visible);

  useEffect(() => {
    if (props.visible) {
      setHasMounted(true);
    }
  }, [props.visible]);

  if (!hasMounted) {
    return null;
  }

  return (
    <AuthLoadingProvider
      value={{ visible: props.visible, onClose: props.onClose }}
    >
      <DynamicAuthManagementContent {...props} />
    </AuthLoadingProvider>
  );
};

export { AuthManagementDynamic };
